// @givmo/auth — the post-sign-in routing DECISION (pure function).
//
// Ported VERBATIM from the webapp `SignInCubit._checkUserStatus` + `AuthStatus` enum + the
// `navigateAfterSignIn` switch. Extracted as a pure function so it is unit-testable without Firebase
// or a router, and so every consuming app reaches the SAME decision (then maps the returned state
// onto its own routes). The decision is a function of (company-presence, email-verified,
// company-active) — nothing UI.

import type { AuthRoutingState, CompanyStatus } from './types';

/** The company status string that means "active". Webapp `Consts.activeStatus`. */
export const ACTIVE_COMPANY_STATUS = 'active';

/** Inputs to the routing decision — exactly what `_checkUserStatus` reads. */
export interface RoutingInputs {
  /** The company record for the signed-in user, or `null` if the lookup failed/none exists. */
  company: CompanyStatus | null;
  /** Whether the backend reports the user's email as verified. */
  emailVerified: boolean;
  /**
   * True when this sign-in is finishing a provider (Google/Microsoft) registration. Ported from the
   * `isProviderRegistration` branch: a failed company lookup during provider registration routes to
   * `unregistered` (finish sign-up) rather than being treated as a hard failure.
   */
  isProviderRegistration?: boolean;
}

/**
 * Decide where a successful Firebase sign-in should route. Mirrors `_checkUserStatus`:
 *
 *   no company found:
 *     • provider registration  → `unregistered` (go finish company registration)
 *     • otherwise               → caller treats as a sign-in failure (returns `null` here)
 *   company found:
 *     • email not verified      → `emailUnverified`  (OTP screen)
 *     • company not active      → `pendingManualVerification`
 *     • else                    → `authenticated`
 *
 * Returns `null` for the "company lookup failed on a non-provider sign-in" case so the caller can
 * surface the underlying error (the cubit emitted `SignInFailure` there) instead of inventing a
 * route.
 */
export function decideRoutingState(inputs: RoutingInputs): AuthRoutingState | null {
  if (inputs.company === null) {
    return inputs.isProviderRegistration ? 'unregistered' : null;
  }
  if (!inputs.emailVerified) {
    return 'emailUnverified';
  }
  if (inputs.company.status !== ACTIVE_COMPANY_STATUS) {
    return 'pendingManualVerification';
  }
  return 'authenticated';
}
